import { type ApiTaskSettingsModifiers, normalizeTaskSettingsModifiers } from '@kaiizen/kai-pro-api';
import { useAllOrgSettings } from '@kaiizen/kai-pro-api-react-state';
import { useCallback } from 'react';

import { useSettingsAutosave } from './useSettingsAutosave';

export type ModifierRecord = Record<string, ApiTaskSettingsModifiers>;

/**
 * Autosave for the Modifiers section, over the hook it was generalized into (KAIDEV-4345).
 *
 * What is left here is what is particular to modifiers: where they persist, that a new one leads
 * the list, and that a label is trimmed as the Add modal already trims it. Everything the two
 * review rounds hardened - the save chain, the per-field revert, the unmount flush - lives in
 * `useSettingsAutosave` and is shared with every other tab in the surface.
 */
export const useModifierAutosave = (
  persistedModifiers: ModifierRecord,
  {
    validate,
  }: {
    /**
     * Rejects a change before it is sent, returning why. A rejected edit stays on screen so it
     * can be corrected, but never reaches the server — the stored value remains in effect.
     */
    validate?: (key: string, modifiers: ModifierRecord) => string | undefined;
  } = {},
) => {
  const { allOrgSettingsMutators } = useAllOrgSettings();

  /* A fresh closure every render, which is exactly what `useSettingsAutosave` expects: it reads
     this through a ref rather than closing over it. `useAllOrgSettings` rebuilds its mutators
     object each render, and depending on that identity is what started a save loop once
     (#2787 review). */
  const save = useCallback(
    (modifiers: ModifierRecord) => allOrgSettingsMutators.update({ taskSettings: { modifiers } }),
    [allOrgSettingsMutators],
  );

  /* The server normalizes modifiers on write and on read - `mergeModifierWithDefaults` spreads the
     type's defaults, so adding a Deferred modifier comes back carrying `excludeFromPurchaseOrders`
     and `triggeredByChangeOrder` that the add never sent. Without this the guard pinned that card
     for the life of the page (KAIDEV-4345 review round 4). */
  const toStoredForm = useCallback(
    (record: ModifierRecord): ModifierRecord => normalizeTaskSettingsModifiers(record) ?? record,
    [],
  );

  const { draft, errorByKey, updateEntry, commitEntry, addEntry, focusEntry } = useSettingsAutosave<
    ApiTaskSettingsModifiers
  >(persistedModifiers, { save, validate, toStoredForm });

  /** Applies part of a modifier. Toggles pass `immediate`; a label leaves it to the debounce. */
  const updateModifier = useCallback(
    (key: string, change: Partial<ApiTaskSettingsModifiers>, options?: { immediate?: boolean }) => {
      const existing = draft[key];
      if (!existing) return;
      updateEntry(key, previous => ({ ...(previous ?? existing), ...change }), options);
    },
    [draft, updateEntry],
  );

  /**
   * Adds a modifier and saves it at once.
   *
   * The key doubles as the line-item tag, so it is minted by the caller and never derived from
   * the label: renaming is the criteria's own recovery path for a mislabeled modifier, and a
   * label-derived tag would orphan every line item carrying it the first time anyone used it.
   */
  const addModifier = useCallback(
    (modifier: ApiTaskSettingsModifiers & { key: string }) => {
      const { key, ...record } = modifier;
      // New modifiers lead the list, as the criteria require.
      addEntry(key, record);
      return key;
    },
    [addEntry],
  );

  /**
   * Blur commits whatever is pending for that modifier, and trims the label as the Add modal
   * already does. A trim is itself a change, so it leaves something pending to commit.
   */
  const commitModifier = useCallback(
    (key: string) =>
      commitEntry(key, previous => {
        if (!previous) return undefined;
        const trimmed = previous.displayName?.trim();
        // `undefined` for "nothing to trim": a label merely tabbed through is not worth a write.
        return trimmed === previous.displayName ? undefined : { ...previous, displayName: trimmed };
      }),
    [commitEntry],
  );

  return {
    draft,
    errorByKey,
    updateModifier,
    commitModifier,
    addModifier,
    /** Marks a modifier's label as being typed in, so a refresh leaves it alone until blur. */
    focusModifier: focusEntry,
  };
};
